<template>
  <div
    :class="{ '-shifted': isProgressbarExpanded }"
    class="search-navigation-block"
  >
    <div class="search-navigation-block-controls">
      <div class="back-icon" @click="backToSearch()">
        <BaseSpriteIcon icon-name="ico-arrow-back" custom-class="me-1" />
        <span class="back-to-search">{{
          $t('SearchNavigation.label.backToSearch')
        }}</span>
      </div>
      <div class="search-result-nav">
        <div
          class="result-nav-prev"
          :class="{ disabled: isDisablePrevious }"
          @click="getPreviousSent()"
        >
          <BaseSpriteIcon icon-name="ico-arrow-chevron" />
        </div>
        <div v-if="isNothingFound" class="no-book-results">
          {{ $t('SearchNavigation.label.nothingFoundInsideBook') }}
        </div>
        <div v-else class="result-nav-curr">
          <span>{{ currentNavigatePosition }}</span>
          <span class="devider">/</span>
          <span>{{ docsCount }}</span>
        </div>
        <div
          class="result-nav-next"
          :class="{ disabled: isDisableNext }"
          @click="getNextSent()"
        >
          <BaseSpriteIcon icon-name="ico-arrow-chevron" />
        </div>
      </div>
      <div class="close-icon" @click="closeSearchNavigation()">
        <span class="close-search-label">{{
          $t('SearchNavigation.label.closeSearch')
        }}</span>
        <BaseSpriteIcon icon-name="ico-close" />
      </div>
    </div>
    <div class="search-navigation-field-wrapper">
      <SearchLoader>
        <sw-book-search
          :language="currentLanguage"
          :interface-language="systemLang"
          :profile="brand"
          :manager="manager"
          :environment="env"
          :full-access="!isUserGuest"
          :current-publication="publicationId"
          @searchWidgetEvent="searchWidgetEventHandler"
        />
      </SearchLoader>
    </div>
  </div>
</template>

<script>
import AppStateEnum from '@/enums/AppStateEnum';
import BookScrollEnum from '@/enums/BookScrollEnum';
import BaseSpriteIcon from '@/components/base/BaseSpriteIcon/BaseSpriteIcon';
import GoogleAnalyticsUtils from '@/services/utils/GoogleAnalyticsUtils';
import SearchNavigationEventEnum from '@/enums/SearchNavigationEventEnum';
import LoggerFactory from '@/services/utils/LoggerFactory';
import PublicationNavigateLogicService from '@/services/PublicationLogic/PublicationNavigateLogicService';
import MarkerUtils from '@shared/publication/dom-utils/marker-utils.mjs';
import SearchHelpers from '@/services/utils/searchHelpers';

import SearchLoader from '@/components/SearchLoader.vue';

const logger = LoggerFactory.getLogger('SearchNavigation.vue');

const NAV_BAR_LIVING_TIME = 15 * 60 * 1000;
let timeout = null;
export default {
  name: 'SearchNavigation',
  components: {
    BaseSpriteIcon,
    SearchLoader
  },
  props: {
    publicationId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      noResultsForCurrentBook: false,
      allowedReturnToReadingPosition: true,
      sortedNavigationList: [],
      manager: {
        getDocByDocId: {}
      }
    };
  },
  computed: {
    brand() {
      return this.$store.getters['ContextStore/brand'];
    },
    env() {
      return this.$store.getters['ContextStore/env'];
    },
    currentLanguage() {
      return this.$store.getters['PublicationStore/getBookLanguage'];
    },
    systemLang() {
      return this.$store.getters['ContextStore/getSystemLanguage'];
    },
    isUserGuest() {
      return this.$store.getters['UserStore/isGuestUser'];
    },
    isDisablePrevious() {
      return this.navigationIndex === 0;
    },
    isDisableNext() {
      return (
        this.isNothingFound ||
        this.docsCount === this.navigationIndex + 1 ||
        !this.docsCount
      );
    },
    isOpenedInsideBook() {
      const isSearchOpenedFromToolbar = this.$store.getters[
        'ContextStore/getIsBookSearchShown'
      ];
      return isSearchOpenedFromToolbar;
    },
    currentNavigatePosition() {
      return this.isNothingFound || !this.docsCount
        ? 0
        : this.navigationIndex + 1;
    },
    navigationIndex() {
      return this.$store.getters['BookStore/getNavigationIndex'];
    },
    navigationList() {
      return this.$store.getters['SwNavigationStore/getNavigationList'];
    },
    docsCount() {
      return this.$store.getters['SwNavigationStore/getTotalDocsCount'] || 0;
    },
    isNothingFound() {
      return (
        this.$store.getters['SwSearchStore/getFoundNothing'] ||
        this.noResultsForCurrentBook
      );
    },
    publicationType() {
      return this.$store.getters['OpenParameterStore/getPublicationType'];
    },
    searchText() {
      return this.$store.getters['SwSearchStore/getSearchText'];
    },
    isAudioPlaying() {
      return this.$store.getters['PlaybackStore/isAudioPlaying'];
    },
    appState() {
      return this.$store.getters['ContextStore/appState'];
    },
    isProgressbarExpanded() {
      return !this.$store.getters['ContextStore/getIsProgressbarCompact'];
    }
  },
  watch: {
    publicationId() {
      this.allowedReturnToReadingPosition = false;
    },
    navigationList() {
      this.noResultsForCurrentBook = false;
      this.$_updateSearchNavigation();
    },
    searchText() {
      this.$store.dispatch('BookStore/resetSearchNavigation', {
        bookId: this.publicationId
      });
      this.$_emit({
        type: SearchNavigationEventEnum.TEXT_CHANGED
      });
    },
    isAudioPlaying() {
      this.closeSearchNavigation();
    }
  },
  beforeDestroy() {
    this.$_showBackToReadingToaster();
  },
  mounted() {
    this.$_startCloseSearchNavigation();
    if (this.isOpenedInsideBook) {
      this.$_pauseAudioIfNeeded();
      this.$store.dispatch('SwSearchStore/setSearchText', '');
      this.$store.dispatch('BookStore/resetSearchNavigation');
    } else {
      this.$_updateSearchNavigation();
    }
  },

  methods: {
    $_showBackToReadingToaster() {
      const isInsideReadingArea = this.$store.getters[
        'ProgressStore/isLastReadingPositionInsideReadingArea'
      ](this.publicationId, this.publicationType);

      const isCompilationOpen = this.$store.getters[
        'PublicationStore/isCompilationOpen'
      ];

      const shouldShowToaster =
        this.allowedReturnToReadingPosition &&
        !isInsideReadingArea &&
        !isCompilationOpen &&
        !this.isAudioPlaying &&
        this.appState === AppStateEnum.PRESENT_PUBLICATION;

      if (!shouldShowToaster) {
        return;
      }

      this.$store.dispatch('ManagePopupStore/openToaster', {
        returnToReadingPosition: true,
        toasterIcon: 'ico-book-new',
        persistent: true,
        publicationId: this.publicationId,
        onCloseHandler: () => {
          const scrollEvent = {
            type: BookScrollEnum.STOP,
            data: {}
          };
          this.$store.commit(
            'PublicationStore/setBookScrollEvent',
            scrollEvent
          );
        }
      });
    },
    closeSearchNavigation() {
      if (this.isOpenedInsideBook) {
        this.$store.dispatch('SwSearchStore/setSearchText', '');
        this.$store.dispatch('BookStore/resetSearchNavigation');
      }

      this.$_emit({
        type: SearchNavigationEventEnum.CLOSE_SEARCH_NAV_BAR
      });

      this.$_resetSearchNavigation();
      this.$_clearTimeout();
    },

    $_pauseAudioIfNeeded() {
      if (this.isAudioPlaying) {
        this.$store.dispatch('PlaybackStore/pause');
      }
    },

    backToSearch() {
      GoogleAnalyticsUtils.trackEvent('advancedSearch', 'backToSearchFromBook');
      this.$_emit({
        type: SearchNavigationEventEnum.BACK_TO_SEARCH
      });
      this.$_openSearch();
    },

    getPreviousSent() {
      this.$_startCloseSearchNavigation();
      if (this.isDisablePrevious) {
        return;
      }

      const navigationIndex =
        this.navigationIndex - 1 >= 0 ? this.navigationIndex - 1 : 0;

      this.$_emit({
        type: SearchNavigationEventEnum.NAVIGATE_PREV_SEARCH_SENT
      });

      const doc = this.sortedNavigationList[navigationIndex];
      this.manager.getDocByDocId = {
        docId: doc.docId
      };

      this.$store.commit('BookStore/updateSearchNavigation', {
        searchNavigation: {
          docId: doc.docId,
          navigateIndex: navigationIndex
        }
      });
    },

    getNextSent() {
      this.$_startCloseSearchNavigation();
      if (this.isDisableNext) {
        return;
      }

      const navigationIndex =
        this.navigationIndex + 1 <= this.docsCount
          ? this.navigationIndex + 1
          : this.navigationIndex;

      this.$_emit({
        type: SearchNavigationEventEnum.NAVIGATE_NEXT_SEARCH_SENT
      });

      const doc = this.sortedNavigationList[navigationIndex];
      this.manager.getDocByDocId = {
        docId: doc.docId
      };

      this.$store.commit('BookStore/updateSearchNavigation', {
        searchNavigation: {
          docId: doc.docId,
          navigateIndex: navigationIndex
        }
      });
    },
    async searchWidgetEventHandler(payload) {
      const { type, data } = payload;
      let openParams = {};
      switch (type) {
        case 'getDocByDocId': {
          if (!data?.publication || !data?.publication?.publicationId) {
            return;
          }

          if (data.publication.publicationId !== this.publicationId) {
            this.$_emit({
              type: SearchNavigationEventEnum.NO_RESULTS_INSIDE_BOOK
            });
            this.noResultsForCurrentBook = true;
            return;
          }
          openParams = SearchHelpers.buildOpenParameters(
            data.publication,
            data.locator,
            data.query,
            false
          );
          await PublicationNavigateLogicService.openPublication(
            this.$store,
            this.$router,
            openParams
          );
          break;
        }
      }
    },
    $_updateSearchNavigation() {
      if (!this.navigationList?.length) {
        return;
      }
      let startIndex = 0;
      let docIds = [...this.navigationList];

      if (!this.isOpenedInsideBook) {
        startIndex = this.$store.getters[
          'SwNavigationStore/getNavigationIndex'
        ];
      } else {
        docIds = JSON.parse(JSON.stringify(docIds));
        docIds = docIds.sort(
          (a, b) =>
            MarkerUtils.getParaNum(a.paraId || '0') -
            MarkerUtils.getParaNum(b.paraId || '0')
        );
      }

      this.sortedNavigationList = docIds;

      this.$store.commit('BookStore/setSearchNavigation', {
        bookId: this.publicationId,
        searchNavigation: {
          navigateIndex: startIndex,
          docId: this.sortedNavigationList[startIndex],
          navigateList: this.sortedNavigationList,
          totalResults: this.docsCount
        }
      });

      const doc = this.sortedNavigationList[startIndex];
      this.manager.getDocByDocId = {
        docId: doc.docId
      };
    },

    $_emit(payload) {
      this.$emit('searchNavigationEvent', payload);
    },

    $_startCloseSearchNavigation() {
      const self = this;
      this.$_clearTimeout();
      timeout = setTimeout(function() {
        self.closeSearchNavigation();
      }, NAV_BAR_LIVING_TIME);
    },

    $_clearTimeout() {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    },

    $_openSearch() {
      this.$router.push({ name: AppStateEnum.SEARCH });
    },

    $_errorHandler(error) {
      logger.fatal(`Error in SearchNavigation: ${error.stack}`);
      this.$_emit({ type: SearchNavigationEventEnum.ERROR, data: { error } });
    },

    $_resetSearchNavigation() {
      try {
        this.$store.dispatch('BookStore/resetSearchNavigation', {
          bookId: this.publicationId
        });
      } catch (error) {
        this.$_errorHandler(error);
      }
    }
  }
};
</script>

<style lang="less" src="./SearchNavigation.less" />
